import { useMutation } from '@apollo/client';
import type { User } from '@interfaces/user';
import { ListItem, TextField } from '@mui/material';
import CreateNewModal from '@ovComponents/3-pattern/legacyCreateNewModal/createNewModal';
import OrganizationSelect from '@ovComponents/3-pattern/legacyOrganizationSelect/organizationSelect';
import { UserContext } from '@providers/userContextProvider';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useLocalization } from '../../hooks/useLocalization';

import { CREATE_USER } from './newClientModal.query';

export const NewClient = ({
  afterCreate,
  buttonType,
  modalButton,
  fullWidth,
  customMargin,
  forceOpen,
  newClient,
  icon,
}: {
  afterCreate?: () => void;
  newClient?: (user: User) => void;
  buttonType?: 'BUTTON' | 'ICON' | 'NONE';
  modalButton?: string;
  fullWidth?: boolean;
  customMargin?: string;
  forceOpen?: boolean;
  icon?: any;
}) => {
  const { t } = useTranslation(['clients']);
  const { languageName } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [client, setClient] = useState({
    firstName: '',
    lastName: '',
    primaryEmail: '',
    organizationId: activeOrganization.id,
    language: languageName,
    timezone: 'America/Edmonton',
  });
  const [initialState] = useState({
    firstName: '',
    lastName: '',
    primaryEmail: '',
    organizationId: activeOrganization.id,
    language: languageName,
    timezone: 'America/Edmonton',
  });
  const [createClient, { loading }] = useMutation(CREATE_USER, {
    variables: {
      input: {
        ...client,
      },
    },
  });

  useEffect(() => {
    setClient({ ...client, organizationId: activeOrganization.id });
  }, [activeOrganization]);

  const updateField = (event: any) => {
    const key: 'firstName' | 'lastName' | 'primaryEmail' = event.target.id;
    setClient({ ...client, [key]: event.target.value as string });
  };

  const createOrg = async () => {
    const response = await createClient();
    if (forceOpen) {
      setOpen(forceOpen);
    } else if (response && response.data && !response.errors) {
      navigate(`/clients/${response.data.createUser.user.id}`);
    }
    if (afterCreate) {
      afterCreate();
    }
    if (newClient) {
      newClient(response.data.createUser.user);
    }
  };

  return (
    <CreateNewModal
      loading={loading}
      title={t('addModal.title')}
      onSubmit={createOrg}
      buttonType={buttonType}
      modalButton={modalButton}
      icon={icon}
      forceOpen={open}
      fullWidth={fullWidth}
      state={client}
      initialState={initialState}
      sx={customMargin ?? { float: 'right', m: 1 }}
    >
      <ListItem>
        <TextField
          label={t('addModal.firstName')}
          sx={{ width: '100%' }}
          value={client.firstName}
          onChange={updateField}
          id='firstName'
          autoFocus
        />
      </ListItem>
      <ListItem>
        <TextField
          label={t('addModal.lastName')}
          sx={{ width: '100%' }}
          value={client.lastName}
          onChange={updateField}
          id='lastName'
        />
      </ListItem>
      <ListItem>
        <TextField
          label={t('addModal.email')}
          sx={{ width: '100%' }}
          value={client.primaryEmail}
          onChange={updateField}
          id='primaryEmail'
          type='email'
        />
      </ListItem>
      <ListItem>
        <OrganizationSelect
          value={client.organizationId}
          label={t('addModal.organization')}
          onChange={(event: any) =>
            setClient({
              ...client,
              organizationId: event.target.value as string,
            })
          }
        />
      </ListItem>
    </CreateNewModal>
  );
};
